<template lang="pug">
    MatchMedia(v-slot='{ screenSM }')
        div.generic-upload-control(:class='getTheClass(!screenSM)')
            div.generic-upload-control__container
                div.generic-upload-control__items
                    div.generic-upload-control__item.generic-upload-control__drop-zone(@click='clickDropzone' @dragover.prevent='dragOver' @dragleave.prevent='dragLeave' @drop.prevent='drop($event)')
                        input(type='file' :accept='acceptTypesString' :multiple='!single' @change='inputFiles' ref='uploadInput')

                        svg-icon(iconId='plus')
                        div.generic-upload-control__drop-zone-texts
                            div(v-if='screenSM')
                                span.generic-upload-control__drop-text {{ dropYourFilesText }}, or&nbsp;
                                span.generic-upload-control__click-text click to browse
                            div(v-else)
                                span.generic-upload-control__click-text Click to browse

                            div.generic-upload-control__size-text(:style='sizeTextStyle')
                                span 1600 x 1200 (4 : 3) recommended, up to 10MB each.
                                resize-sensor(@resize='sizeTextResize')

                        resize-sensor(@resize='dropzoneResize')

                    div.generic-upload-control__inline(v-if="modeInline" v-show="files.length")
                        div.generic-upload-control__inline-item(v-for='(file, fileIdx) in files')
                            div.generic-upload-control__inline-image(@click='clickFile(file, screenSM)')
                                img(v-if="file.type == 'image'" :src="file.url || file.previewUrl")
                                video-player-control(v-else-if="file.type == 'video'" :asset="{ ...file, url: file.url || file.previewUrl }" :fillMode="true" :clickToPlay="false" :hideControls="true")
                            div.generic-upload-control__inline-description
                                div.generic-upload-control__description-title
                                    span.description-title__title(v-if="file.file && file.file.name") {{ file.file.name }}
                                    span.description-title__progress {{ getFileProgressToShow(file) }}% done
                                div.generic-upload-control__description-progress
                                    progress-line-control(:value="getFileProgressToShow(file)")
                                div.generic-upload-control__description-size-message
                                    div.generic-upload-control__description-size {{ getFormattedFileSize(file) }}
                                    div(v-if="file.message" :class="getFileMessageClass(file)" v-html="file.message")
                            div.generic-upload-control__inline-actions
                                div.inline-actions__trash(@click='removeFileByIdx(fileIdx)')
                                    svg-icon(iconId="trash-can")

                    template(v-else)
                        div.generic-upload-control__item.generic-upload-control__card(v-for='(file, fileIdx) in files' :class='getCardClass(file)')
                            div.generic-upload-control__card-image(v-if='file.type == "image"' @click='clickFile(file, screenSM)')
                                img(v-if='file.url' :src='file.url')
                                img.generic-upload-control__card-preview(v-else-if='file.previewUrl' :src='file.previewUrl')
                            div.generic-upload-control__card-video(v-else-if='file.type == "video"')
                                video-player-control(
                                  v-if='file.url'
                                  :key="file.url"
                                  :asset='file'
                                  :fillMode='true'
                                  :clickToPlay='false'
                                  @click='clickFile(file, screenSM)'
                                  :hideControls='file.insertProgress !== undefined'
                                )
                                video-player-control.generic-upload-control__card-preview(
                                  v-else-if='file.previewUrl'
                                  :key="file.previewUrl"
                                  :asset='{ ...file, url: file.previewUrl }'
                                  :fillMode='true'
                                  :clickToPlay='false'
                                  :hideControls='true'
                                )
                                div.generic-upload-control__card-message(v-if='file.insertProgress && file.message')
                                    .alert(v-html="file.message" :class="[`alert-${file.messageType || 'info'}`]")
                                div.generic-upload-control__card-progress(v-if='file.insertProgress')
                                    progress-circle-control(:value='file.insertProgress')

                            div.generic-upload-control__card-message(v-if='file.message')
                                .alert(v-html="file.message" :class="[`alert-${file.messageType || 'info'}`]")

                            div.generic-upload-control__card-progress(v-if='file.hasOwnProperty("progress")')
                                progress-circle-control(:value='file.progress')

                            div.generic-upload-control__card-actions
                                div.generic-upload-control__card-remove-button(v-if='screenSM' @click='removeFileByIdx(fileIdx)')
                                    svg-icon(iconId='close-cross')
                                select-option-button-control(v-else :options='getCardOptions(file.url)' @select='selectCardOption($event, fileIdx)')
</template>

<script>
import {setTimeoutPromise} from 'utils/vue/lib/utils'
import { MatchMedia } from 'vue-component-media-queries'

export default {
    name: 'generic-upload-control',
    components: {
        MatchMedia,
    },
    props: {
        value: {
            type: Array,
        },
        uploadHandler: {
            type: Function,
            required: true,
        },
        mode: {
            type: String,
            validate: v => !v || ['default', 'grid', 'inline'].includes(v),
            default: 'default',
        },
        single: {
            type: Boolean,
        },
        clickFileHandler: {
            type: Function,
        },
        allowImage: {
            type: Boolean,
            default: true,
        },
        allowVideo: {
            type: Boolean,
        },
    },
    data() {
        return {
            files: [],

            previewMakerActive: false,
            uploaderActive: false,

            sizeTextVisible: null,
            dropzoneWidth: 0,
            sizeTextWidth: 0,

            dragging: false,

            updateQueue: [],
            pollingDelay: 5000,
        }
    },
    computed: {
        modeGrid() {
            return this.mode == 'grid'
        },

        modeInline() {
            return this.mode === 'inline'
        },

        sizeTextStyle() {
            return {
                'height': this.sizeTextVisible ? null : 0,
                'position': this.sizeTextVisible ? 'relative' : 'absolute',
            }
        },

        acceptTypesString() {
            const accept = []
            if (this.allowImage) {
                accept.push('image/*')
            }
            if (this.allowVideo) {
                accept.push('video/*')
            }
            return accept.join(',')
        },

        cardOptions() {
            return [
                { name: 'Edit', value: 'edit' },
                { name: 'Delete', value: 'delete' },
            ]
        },

        dropYourFilesText() {
            const allowedTexts = []

            if (this.allowImage) {
                allowedTexts.push('image')
            }
            if (this.allowVideo) {
                allowedTexts.push('video')
            }

            return `Drop your ${allowedTexts.length > 1 ? 'file' : allowedTexts[0]}${this.single ? '' : 's'} here`
        },
    },
    watch: {
        files: {
            handler(val) {
                this.$emit('input', val.filter(i => i.id).map(i => this.transformOut(i)))
            },
            deep: true,
        },
    },
    methods: {
        checkUploadActive() {
            return this.files.some(f => f.upload)
        },

        clickDropzone() {
            this.$refs.uploadInput.click()
        },
        inputFiles(e) {
            e.currentTarget.files.forEach(f => {
                this.uploadFile(f)
            })
            this.$refs.uploadInput.value = ''
        },

        dragOver() {
            this.dragging = true
        },
        dragLeave() {
            this.dragging = false
        },
        drop(e) {
            const dropFiles = [...e?.dataTransfer.files].filter(this.filterMime)

            if (dropFiles.length) {
                const files = this.single ? [dropFiles[0]] : dropFiles
                files.forEach(f => this.uploadFile(f))
            }

            this.dragging = false
        },

        filterMime(file) {
            const t = file.type

            if (t.startsWith('image/') && this.allowImage) {
                return true
            }
            if (t.startsWith('video/') && this.allowVideo) {
                return true
            }

            return false
        },

        uploadFile(file, replaceId) {
            if (this.single) {
                this.removeAllFiles()
            }

            const fileItem = {
                id: null,
                file,
                mimeType: file.type,
                type: file.type.split('/')[0],
                url: null,
                previewUrl: null,
                upload: true,
                progress: -1,
                meta: {},
                frames: []
            }

            if (replaceId) {
                const idx = this.files.findIndex(f => f.id == replaceId)
                if (idx >= 0) {
                    this.removeFileByIdx(idx)
                    this.files.splice(idx, 0, fileItem)
                }
                else {
                    this.files.push(fileItem)
                }
            }
            else {
                this.files.push(fileItem)
            }

            this.invokeMakePreview()
            this.invokeUploader()
        },

        async uploadVideoFileChanges(file, replaceId, updates) {
            file = this.files.find(f => f.id === replaceId)
            const canUpdate = _.some(updates?.data || {}, i => !!i)
            if (canUpdate) {
                this.updateQueue.unshift({id: replaceId, file, updates})
                await this.invokeUpdater(file)
            }
        },

        removeFileByIdx(idx) {
            const fileItem = this.files[idx]
            if (fileItem.hasOwnProperty('cancel')) {
                fileItem.cancel()
            }
            this.files.splice(idx, 1)
            this.$emit('remove', this.transformOut(fileItem))
        },
        removeFile(file) {
            const idx = this.files.findIndex(f => f.id == file.id)
            if (idx >= 0) {
                this.removeFileByIdx(idx)
            }
        },
        removeAllFiles() {
            this.files.forEach(f => {
                if (f.cancel) {
                    f.cancel()
                }
                this.$emit('remove', this.transformOut(f))
            })
            this.files = []
        },

        invokeMakePreview() {
            if (this.previewMakerActive) {
                return
            }

            const fileItem = this.files.find(f => !f.url && !f.previewUrl && !f.previewFailed)

            if (fileItem) {
                this.previewMakerActive = true

                const url = URL.createObjectURL(fileItem.file)

                this.unsetFileMessage(fileItem)
                this.preloadMedia(url, fileItem.type, () => {
                    fileItem.previewUrl = url
                }, (e) => {
                    this.setFileErrorMessage(fileItem, e)
                    this.setFileAttributes(fileItem, {previewFailed: true})
                }, () => {
                    this.previewMakerActive = false
                    this.invokeMakePreview()
                })
            }
        },

        invokeUploader(forced) {
            if (!forced && this.uploaderActive) {
                return
            }

            const uploadFile = this.files.find(f => f.upload && !f.processing)

            if (uploadFile) {
                if (uploadFile.update) {
                    this.invokeUpdater()
                    return
                }

                if (uploadFile.file.size > window.addiesaas.config.upload_max_filesize) {
                    const formattedFileSize = this.getFormattedSize(uploadFile.file.size)
                    const formattedMaxSize = this.getFormattedSize(window.addiesaas.config.upload_max_filesize)
                    const message = `The file size is ${formattedFileSize}, which exceeds allowed maximum of ${formattedMaxSize}.`
                    this.setFileMessage(uploadFile, message, 'danger')
                    this.unsetFileAttributes(uploadFile, ['progress', 'insertProgress'])
                    this.setFileAttributes(uploadFile, {upload: false, update: false})
                    return
                }

                this.uploaderActive = true
                this.unsetFileMessage(uploadFile)

                const { promise, cancel } = this.uploadHandler(uploadFile.file, this.progressCallback)
                uploadFile.cancel = cancel

                this.handleUploadPromise(promise, uploadFile)
            }

        },

        handleUploadPromise(promise, file, updates, isPolling, immediate){
            let needsPolling = false
            const pluckFileProperties = ['id', 'url', 'size', 'frames', 'meta', 'mimeType', 'processing', 'original_url', 'watermark_options']

            promise
                .then(({data}) => {
                    //
                    needsPolling = !!data.processing
                    if (needsPolling) {
                        if (!isPolling) {
                            if (updates) {
                                this.showUploadPollingMessages(file, updates)
                            }
                            Object.assign(file, _.pick(data, ['id', 'processing']))
                        }
                        this.pollUploadStatus(file, updates, immediate)

                    } else {
                        this.unsetFileMessage(file)
                        this.preloadMedia(data.url, file.type, () => {
                            Object.assign(file, _.pick(data, pluckFileProperties))
                        })
                    }
                })
                .catch(e => {
                    this.setFileErrorMessage(file, e)
                })
                .finally(() => {
                    if (!needsPolling) {
                        this.unsetFileAttributes(file, ['progress', 'insertProgress'])
                        this.setFileAttributes(file, {upload: false, update: false})
                    }
                    this.uploaderActive = false
                    this.invokeUploader()
                })
        },

        pollUploadStatus(file, updates, immediate) {
            this.$nextTick(async () => {
                if (!immediate) {
                    await setTimeoutPromise(this.pollingDelay)
                }
                const options = {get: true}
                const { promise, cancel } = this.uploadHandler(file, this.progressCallback, null, options, {})
                file.cancel = cancel
                this.handleUploadPromise(promise, file, updates, true)
            })
        },

        async invokeUpdater(file, forced) {
            if ((!forced && this.uploaderActive) || !this.updateQueue.length) {
                if (this.uploaderActive && file) {
                    this.setFileAttributes(file, {progress: -1, upload: true, update: true, queue: true})
                    this.showUploadPollingMessages(file, null, [
                        {delay: 0, show: 5000, message: 'Waiting in queue'}
                    ], ()=> file.queue)
                }
                return
            }

            const queueData = this.updateQueue.pop()
            if (!queueData) {
                return
            }

            const {id, updates, options = {}, config} = queueData || []
            const updatingFile = this.files.find(f => f.id === id)

            if (updatingFile) {
                options.update = true
                this.unsetFileMessage(updatingFile)
                this.setFileAttributes(updatingFile, {progress: -1, upload: true, update: true, queue: false})
                this.uploaderActive = true
                const { promise, cancel } = this.uploadHandler(updatingFile, this.progressCallback, updates, options, config)
                updatingFile.cancel = cancel

                this.handleUploadPromise(promise, updatingFile, updates)

                //@todo: [INFO] return the promise to keep showing the edit/configuration tabs
                //return promise
            } else {
                this.invokeUploader()
            }
        },

        getUploadPollingMessages(file, updates, messages) {
            if (messages) {
                return messages
            }
            const isTranscode = !updates && file.mimeType !== 'video/mp4'
            messages = [
                {delay: 1000, show: 10000, message: 'Your video is being processed. Please stay on the page until it is complete. '},
            ]
            let addedMessages = [
                {delay: 10000, show: 10000, message: 'Your video is taking a time to process. Please stay on the page.'},
                {delay: 15000, show: 15000, message: 'Your video is still being processed. Please stay on the page until it is complete. '},
            ]
            if (isTranscode) {
                addedMessages = [
                    {delay: 10000, show: 10000, message: 'Your video is converted to a better format. It may take some time to complete.'},
                    {delay: 10000, show: 10000, message: 'Your video is taking a time to convert. Please stay on the page.'},
                    {delay: 15000, show: 15000, message: 'Your video is still being converted. Please stay on the page until it is complete. '},
                ]
            } else {
                if (updates) {
                    if (updates?.data?.crop) {
                        addedMessages.unshift(
                            {delay: 10000, show: 10000, message: 'Your video is now being cropped. Please stay on the page until it is complete.'}
                        )
                    }
                    if (updates?.data?.trim) {
                        addedMessages.unshift(
                            {delay: 10000, show: 10000, message: 'Your video is now being trimmed. Please stay on the page until it is complete.'},
                        )
                    }
                }
            }
            messages.splice(1, 0, ...addedMessages)
            return messages
        },
        showUploadPollingMessages(file, updates, setMessages, continueIf) {
            if (file.type !== 'video') {
                return
            }
            if (!continueIf) {
                continueIf = () => file.upload || file.insertProgress
            }
            const messages = this.getUploadPollingMessages(file, updates, setMessages)
            this.$nextTick(async () => {
                for (const message of messages) {
                    if (continueIf()) {
                        await this.showUploadPollingMessage(message, file, continueIf)
                    }
                }
                if (continueIf()) {
                    this.showUploadPollingMessages(file, updates, setMessages, continueIf)
                }
            })
        },
        async showUploadPollingMessage(details, file, continueIf) {
            await this.$nextTick()
            if (continueIf()) {
                const {delay, message, show} = details || {}
                await setTimeoutPromise(delay)
                await this.$nextTick()
                if (continueIf()) {
                    this.setFileMessage(file, message)
                    await setTimeoutPromise(show)
                }
            }
            await this.$nextTick()
            if (file.messageType !== 'danger') {
                this.unsetFileMessage(file)
            }
        },

        progressCallback(percent, file) {
            const cf = this.files.find(f => f.file === file)

            if (cf) {
                cf.progress = percent
                const completed = percent === 100
                if (completed) {
                    cf.progress = -1
                    this.showUploadPollingMessages(cf)
                }
            }
        },

        checkCardClickable(url, screenSM) {
            return screenSM && url && this.clickFileHandler
        },

        clickFile(file, screenSM) {
            if (file.insertProgress !== undefined) {
                return
            }
            if (this.checkCardClickable(file.url, screenSM)) {
                this.clickFileHandler(this.transformOut(file), file)
            }
        },

        selectCardOption(option, fileIdx) {
            switch (option.value) {
                case 'edit':
                    this.clickFile(this.files[fileIdx], true)
                    break
                case 'delete':
                    this.removeFileByIdx(fileIdx)
                    break
            }
        },

        transformOut(file) {
            return _.pick(file, ['id', 'url', 'type', 'file', 'meta', 'frames'])
        },

        getCardOptions(url) {
            const cardClickable = this.checkCardClickable(url, true)
            return this.cardOptions.filter(o => cardClickable || o.value != 'edit')
        },

        getTheClass(forceGrid) {
            const mode = (forceGrid && 'grid') || this.mode

            return {
                [`generic-upload-control--mode-${mode}`]: true,
                'generic-upload-control--dragging': this.dragging,
                'generic-upload-control--card-clickable': !!this.clickFileHandler,
            }
        },
        getCardClass(file) {
            return {
                'generic-upload-control__card--upload': file.upload || !file.url,
                'generic-upload-control__card--empty': !file.previewUrl && !file.url,
                'generic-upload-control__card--insert': file.insertProgress !== undefined,
            }
        },

        updateSizeTextDisplay() {
            this.sizeTextVisible = this.dropzoneWidth > this.sizeTextWidth + 20
        },

        dropzoneResize(s) {
            this.dropzoneWidth = s.width
            this.updateSizeTextDisplay()
        },
        sizeTextResize(s) {
            this.sizeTextWidth = s.width
            this.updateSizeTextDisplay()
        },

        getFormattedFileSize(file) {
            return this.getFormattedSize(file.file?.size || file.size)
        },

        getFormattedSize(size) {
            if (size) {
                const sizes = ['Bytes', 'KB', 'MB', 'GB']
                const i = Math.floor(Math.log(size) / Math.log(1000))

                return parseFloat((size / Math.pow(1000, i)).toFixed(2)) + sizes[i]
            }

            return ''
        },

        setFiles(files) {
            this.processInsert(files)
            this.files = files
        },
        processInsert(files) {
            files.forEach(file => {
                const {insert, type} = file
                if (insert) {
                    _.unset(file, 'insert')
                    if (type === 'video') {
                        file.insertProgress = -1
                        const promise = async () => ({data: file})
                        this.handleUploadPromise(promise(), file, {}, null, true)
                    }
                }
            })
        },
        setFileAttributes(file, attrs) {
            _.forOwn(attrs, (value, key) => {
                Vue.set(file, key, value)
            })
        },
        unsetFileAttributes(file, attrs) {
            _.forOwn(attrs, (key) => {
                Vue.delete(file, key)
            })
        },
        setFileMessage(file, message, messageType = 'info') {
            this.setFileAttributes(file, {message, messageType})
        },
        unsetFileMessage(file) {
            this.unsetFileAttributes(file, ['message', 'messageType'])
        },
        setFileErrorMessage(file, e) {
            let message
            if (e?.response?.status == 413) {
                message = 'File is too large'
            } else {
                const data = e?.response?.data || {}
                const errors = _.filter(_.flattenDeep(_.values(data?.errors || {})))
                message = errors.join(' ') || data?.message || e?.message || 'An error has occurred!'
            }
            this.setFileMessage(file, message, 'danger')
            return message
        },

        preloadMedia(url, type, onload, onerror, oncomplete) {
            if (type == 'video') {
                try {
                    onload()
                    if (oncomplete) {
                        oncomplete()
                    }
                } catch (e) {
                    onerror(e)
                }
                return
            }

            let image = new Image()

            const complete = () => {
                image = null

                if (oncomplete) {
                    oncomplete()
                }
            }

            image.onload = () => {
                onload()
                complete()
            }
            image.onerror = (e) => {
                if (onerror) {
                    onerror(e)
                }
                complete()
            }

            image.src = url
        },

        getFileProgressToShow(file) {
            if (file.hasOwnProperty('progress')) {
                return file.progress >= 0 ? file.progress : 0
            }
            return 100
        },

        getFileMessageClass(file) {
            return {
                'generic-upload-control__description-message': true,
                [`generic-upload-control__description-message--${file.messageType}`]: file.messageType,
            }
        },
    },
}
</script>
